import React from "react";
import { useForm, useFormState } from "react-hook-form";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import Edit from "@mui/icons-material/Edit";
import EngineeringIcon from "@mui/icons-material/Engineering";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import arrayIncludesAny from "../../shared/arrayIncludesAny.js";
import axios from "../axiosClient.js";
import useAuth0Roles from "../hooks/auth0Roles.jsx";
import useAlertSnackbar from "../hooks/useAlertSnackbar.jsx";
import Alert from "./common/Alert.jsx";
import CancelButton from "./common/CancelButton.jsx";
import DialogTitleBar from "./common/DialogTitleBar.jsx";
import FormStringInput from "./common/FormStringInput.jsx";
import ConfirmAction from "./ConfirmAction.jsx";

function EngineeringUserForm({
  // props
  user,
  mutate,
}) {
  const [open, setOpen] = React.useState(false);

  const [error, setError] = React.useState(null);
  // This component doesn't care about the current state of the success message
  const [, setSuccess] = useAlertSnackbar("success", 30_000);
  const [loading, setLoading] = React.useState(false);

  const formTitle = React.useMemo(() => (user?.id ? "Update Engineer" : "Add Engineer"), [user?.id]);
  const disablePropagation = React.useCallback((event) => {
    event.stopPropagation();
  }, []);

  const defaultValues = React.useMemo(
    () => ({
      name: user?.name || "",
      hourlyRate: user?.hourlyRate || "",
      weeklyWorkingHours: user?.weeklyWorkingHours || "",
      weeklyJiraHours: user?.weeklyJiraHours || "",
    }),
    [user]
  );

  const { handleSubmit, control, reset } = useForm({ defaultValues, shouldUnregister: true });
  const { isDirty } = useFormState({ control });

  const handleOpen = React.useCallback(
    (event) => {
      event.stopPropagation();
      setOpen(true);
      reset(defaultValues);
    },
    [defaultValues, reset]
  );

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const onSubmit = React.useCallback(
    async (data) => {
      setLoading(true);

      const payload = {
        ...data,
        hourlyRate: Number(data.hourlyRate),
        weeklyWorkingHours: Number(data.weeklyWorkingHours),
        weeklyJiraHours: Number(data.weeklyJiraHours),
      };

      try {
        if (user) {
          await axios({
            method: "PATCH",
            url: `/api/engineering-users/${user.id}`,
            data: payload,
          });
        } else {
          await axios({
            method: "POST",
            url: `/api/engineering-users`,
            data: payload,
          });
        }

        handleClose();
        if (mutate) {
          mutate();
        }
        setSuccess(`Successfully ${user ? "updated" : "added"} user.`);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
      setLoading(false);
    },
    [user, handleClose, mutate, setSuccess]
  );

  const { roles } = useAuth0Roles();
  const canDelete = React.useMemo(
    () => arrayIncludesAny(roles, ["Scorecard Admin", "Scorecard PLC Admin"]),
    [roles]
  );
  const handleDeleteConfirmation = React.useCallback(async () => {
    if (user) {
      await axios({
        method: "DELETE",
        url: `/api/engineering-users/${user.id}`,
      });
      handleClose();
      if (mutate) {
        mutate();
      }
    }
  }, [user, handleClose, mutate]);

  return (
    <>
      {user ? (
        <Tooltip title="Edit this user" arrow>
          <IconButton
            aria-label="edit-user"
            color="primary"
            onClick={handleOpen}
            data-cy={`edit-user-${user.id}`}
          >
            <Edit />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={handleOpen}
          startIcon={<AddCircleIcon />}
          data-cy="add-user"
        >
          Add Engineer
        </Button>
      )}
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClick={disablePropagation}
        data-cy="user-form"
        slotProps={{
          paper: {
            component: "form",
            onSubmit: handleSubmit(onSubmit),
            noValidate: true,
          },
        }}
      >
        <Alert message={error} setMessage={setError} level="error" data-cy="dialog-error" />
        <DialogTitleBar title={formTitle} Icon={EngineeringIcon} iconColor="secondary" />
        <DialogContent>
          <Grid container spacing={4} sx={{ alignItems: "center" }}>
            <Grid size={12}>
              <FormStringInput
                control={control}
                name="name"
                label="Name"
                required
                rules={{ required: "Please provide a name for this user" }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormStringInput
                control={control}
                name="hourlyRate"
                label="Hourly Rate"
                type="number"
                units="$"
                required
                rules={{
                  required: "Please enter the hourly rate",
                  min: { value: 0, message: "Rate cannot be negative" },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormStringInput
                control={control}
                name="weeklyWorkingHours"
                label="Weekly Working Hours"
                type="number"
                required
                rules={{
                  required: "Please enter weekly working hours",
                  min: { value: 0, message: "Hours cannot be negative" },
                  max: { value: 168, message: "Hours cannot exceed 168" },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormStringInput
                control={control}
                name="weeklyJiraHours"
                label="Weekly Jira Hours"
                type="number"
                required
                rules={{
                  required: "Please enter weekly Jira hours",
                  min: { value: 0, message: "Hours cannot be negative" },
                  max: { value: 168, message: "Hours cannot exceed 168" },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box sx={{ m: 2, mr: "auto" }}>
            {!!user && canDelete && (
              <ConfirmAction
                actionName="Delete"
                objectName="User"
                handleConfirmation={handleDeleteConfirmation}
                confirmKeyword="DELETE"
              />
            )}
          </Box>
          <Box sx={{ m: 2 }}>
            <CancelButton color="secondary" isDirty={isDirty} onClick={handleClose}>
              Cancel
            </CancelButton>
          </Box>
          <Box sx={{ m: 2 }}>
            <Button
              data-cy="submit-user"
              disabled={loading}
              variant="contained"
              color="secondary"
              loading={loading}
              type="submit"
            >
              {user ? "Update" : "Add"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EngineeringUserForm;
